import React, { useCallback, useEffect, useState } from 'react';
import { App, Descriptions, Modal, Table } from 'antd';
import dayjs from 'dayjs';
import { listMyRoomReports, rejectRoomReport, signRoomReport } from '../../api/roomReports';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';
import { ViewIcon } from '../../components/UIKit';

const STATUS_LABEL = { DRAFT: 'DRAFT', SIGNED_BY_STUDENT: 'SIGNED', FINAL: 'FINAL', HOLD: 'HOLD' };
const STATUS_CLS   = { DRAFT: 'tag-orange', SIGNED_BY_STUDENT: 'tag-blue', FINAL: 'tag-green', HOLD: 'tag-red' };

function StatusTag({ status }) {
  const v = String(status || 'DRAFT');
  return <span className={`tag ${STATUS_CLS[v] || 'tag-gray'}`}>{STATUS_LABEL[v] || v}</span>;
}

export default function StudentReports() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMyRoomReports();
      const nextRows = Array.isArray(data) ? data : [];
      setRows(nextRows);
      setViewing((v) => { if (!v?.id) return v; return nextRows.find((r) => r?.id === v.id) || v; });
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger vos rapports de chambre.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(() => { if (document.visibilityState === 'visible') fetchAll(); }, 10000);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchAll(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisibility); };
  }, [fetchAll]);

  useEffect(() => {
    const onRefresh = () => {
      fetchAll();
    };
    window.addEventListener('reports:refresh', onRefresh);
    return () => window.removeEventListener('reports:refresh', onRefresh);
  }, [fetchAll]);

  const canDecide = (r) => r && r.status !== 'FINAL' && r.status !== 'HOLD' && !r.student_signed_at && !r.student_rejected_at;

  const onSign = async (r) => {
    try { setActionLoading(true); await signRoomReport(r.id); message.success('Rapport accepté et signé.'); setViewing(null); await fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Signature échouée.'), placement: 'topRight' }); }
    finally { setActionLoading(false); }
  };

  const onReject = async (r) => {
    try { setActionLoading(true); await rejectRoomReport(r.id); message.success('Rapport rejeté.'); setViewing(null); await fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Rejet échoué.'), placement: 'topRight' }); }
    finally { setActionLoading(false); }
  };

  return (
    <div className="page">
      <TableCard label="Mes rapports de chambre" loading={loading} rows={rows}>
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Session</th>
              <th>Chambre</th>
              <th>Type</th>
              <th>Statut</th>
              <th>Signé le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.session || '-'}</td>
                <td>{r.room_number || '-'}</td>
                <td>{r.inspection_type || '-'}</td>
                <td><StatusTag status={r.status} /></td>
                <td>{r.student_signed_at ? dayjs(r.student_signed_at).format('YYYY-MM-DD HH:mm') : '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Voir" onClick={() => setViewing(r)}><ViewIcon /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Detail Modal */}
      <Modal title="Détail du rapport" open={!!viewing} onCancel={() => setViewing(null)} width={980} centered destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            {viewing && canDecide(viewing) && (
              <>
                <button className="btn-outline" style={{ color: '#e24b4a', borderColor: 'rgba(226,75,74,0.3)' }} disabled={actionLoading} onClick={() => onReject(viewing)}>Rejeter</button>
                <button className="btn-primary" disabled={actionLoading} onClick={() => onSign(viewing)}>{actionLoading ? 'En cours…' : 'Signer & Accepter'}</button>
              </>
            )}
            <button className="btn-outline" onClick={() => setViewing(null)}>Fermer</button>
          </div>
        }
      >
        {viewing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="Statut"><StatusTag status={viewing.status} /></Descriptions.Item>
              <Descriptions.Item label="Signé le">{viewing.student_signed_at ? dayjs(viewing.student_signed_at).format('DD/MM/YYYY HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="Session">{viewing.session || '-'}</Descriptions.Item>
              <Descriptions.Item label="Chambre">{viewing.room_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="Type">{viewing.inspection_type || '-'}</Descriptions.Item>
              <Descriptions.Item label="HOLD">{viewing.status === 'HOLD' ? 'Oui' : 'Non'}</Descriptions.Item>
              {viewing.status === 'HOLD' && (
                <>
                  <Descriptions.Item label="Raison HOLD" span={2}>{viewing.hold_reason || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Coût HOLD" span={2}>{viewing.hold_cost ?? '-'}</Descriptions.Item>
                </>
              )}
            </Descriptions>
            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />
            <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Room condition</div>
            <Table rowKey={(r) => String(r?.id ?? r?.label ?? Math.random())} size="small" bordered pagination={{ hideOnSinglePage: true, pageSize: 5, size: 'small' }} dataSource={Array.isArray(viewing.RoomReportItems) ? viewing.RoomReportItems : []} columns={[{ title: 'Élément', dataIndex: 'label' }, { title: 'Code', dataIndex: 'code', width: 140 }, { title: 'Commentaire', dataIndex: 'comment' }]} locale={{ emptyText: 'Aucune donnée' }} />
            <hr style={{ margin: '2px 0', border: 'none', borderTop: '0.5px solid rgba(0,0,0,0.08)' }} />
            <div><div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Commentaires</div><p style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{viewing.commentaires || '-'}</p></div>
          </div>
        )}
      </Modal>
    </div>
  );
}

